import scheduleData from '@/data/final-exam-schedules.json';
import type { ScheduledCourse } from '@/app/types/interfaces';

type GroupSlot = [date: string, startMinute: number, endMinute: number];
export type FinalExamTerm = {
  id: string;
  label: string;
  status: 'published' | 'archived' | 'class-period' | 'by-section';
  examPeriod: string;
  gridUrl?: string;
  note?: string;
  groupSlots?: Record<string, GroupSlot>;
};

export type DerivedFinal = {
  course: ScheduledCourse;
  examCode: string | null;
  kind: 'scheduled' | 'arranged' | 'project' | 'multiple' | 'none' | 'unresolved';
  date: string | null;
  startMinute: number | null;
  endMinute: number | null;
  label: string;
};

export const FINAL_EXAM_SOURCE = scheduleData.source;
export const FINAL_EXAM_TERMS = scheduleData.terms as FinalExamTerm[];

export function getFinalExamTerm(termId: string | null | undefined) {
  return FINAL_EXAM_TERMS.find((term) => term.id === termId) ?? null;
}

function slot(date: string, startMinute: number, endMinute: number) {
  return { date, startMinute, endMinute };
}

function firstWeeklyMeeting(course: ScheduledCourse) {
  const first = [...course.meetingTimes]
    .filter((meeting) => meeting.day && Number.isFinite(meeting.startMinute))
    .sort((a, b) => {
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      return days.indexOf(a.day) - days.indexOf(b.day) || a.startMinute - b.startMinute;
    })[0];
  return first ?? null;
}

function fall2026ClassHour(course: ScheduledCourse) {
  const first = firstWeeklyMeeting(course);
  if (!first) return null;
  const { day, startMinute } = first;
  if ((day === 'Monday' || day === 'Thursday') && startMinute === 510) return slot('2026-12-15', 480, 660);
  if ((day === 'Wednesday' && startMinute === 510) || (day === 'Friday' && startMinute === 1060)) return slot('2026-12-17', 480, 660);
  if ((day === 'Tuesday' || day === 'Friday') && startMinute === 620) return slot('2026-12-18', 480, 660);
  if ((day === 'Monday' || day === 'Thursday') && startMinute === 730) return slot('2026-12-21', 480, 660);
  if ((day === 'Monday' || day === 'Thursday') && startMinute === 620) return slot('2026-12-22', 480, 660);
  if ((day === 'Tuesday' || day === 'Thursday') && startMinute === 840) return slot('2026-12-16', 720, 900);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 840) return slot('2026-12-17', 720, 900);
  if (((day === 'Tuesday' || day === 'Friday') && startMinute === 510) || (day === 'Wednesday' && startMinute === 620) || (day === 'Friday' && startMinute === 950)) return slot('2026-12-21', 720, 900);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 950) return slot('2026-12-22', 720, 900);
  if ((day === 'Tuesday' || day === 'Friday') && startMinute === 730) return slot('2026-12-15', 960, 1140);
  if ((day === 'Tuesday' || day === 'Thursday') && startMinute === 950) return slot('2026-12-21', 960, 1140);
  if ((day === 'Tuesday' || day === 'Thursday') && (startMinute === 1060 || startMinute >= 1170)) return slot(day === 'Tuesday' ? '2026-12-15' : '2026-12-17', 1200, 1380);
  if ((day === 'Monday' || day === 'Wednesday') && (startMinute === 1060 || startMinute >= 1170)) return slot(day === 'Wednesday' ? '2026-12-16' : '2026-12-21', 1200, 1380);
  return null;
}

function springClassHour(course: ScheduledCourse, year: 2025 | 2026) {
  const first = firstWeeklyMeeting(course);
  if (!first) return null;
  const { day, startMinute } = first;
  const date = (monthDay: string) => `${year}-${monthDay}`;
  const movedToSunday = year === 2026;

  if ((day === 'Monday' || day === 'Thursday') && startMinute === 730) return slot(date(year === 2026 ? '05-07' : '05-08'), 480, 660);
  if ((day === 'Tuesday' || day === 'Friday') && startMinute === 730) return slot(date(movedToSunday ? '05-10' : '05-09'), 480, 660);
  if (((day === 'Monday' || day === 'Thursday') && startMinute === 620) || (day === 'Wednesday' && startMinute === 510) || (day === 'Friday' && startMinute === 1060)) return slot(date(year === 2026 ? '05-11' : '05-12'), 480, 660);
  if (((day === 'Tuesday' || day === 'Friday') && startMinute === 510) || (day === 'Wednesday' && startMinute === 730) || (day === 'Friday' && startMinute === 840)) return slot(date(year === 2026 ? '05-12' : '05-13'), 480, 660);
  if ((day === 'Tuesday' || day === 'Friday') && startMinute === 620) return slot(date(year === 2026 ? '05-13' : '05-14'), 480, 660);
  if ((day === 'Monday' || day === 'Thursday') && startMinute === 510) return slot(date(year === 2026 ? '05-11' : '05-12'), 720, 900);
  if ((day === 'Tuesday' || day === 'Thursday') && startMinute === 840) return slot(date(year === 2026 ? '05-12' : '05-13'), 720, 900);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 950) return slot(date(year === 2026 ? '05-13' : '05-14'), 720, 900);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 840) return slot(date(movedToSunday ? '05-10' : '05-09'), 960, 1140);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 1060) return slot(date(year === 2026 ? '05-11' : '05-12'), 960, 1140);
  if ((day === 'Wednesday' && startMinute === 620) || (day === 'Friday' && startMinute === 950)) return slot(date(year === 2026 ? '05-12' : '05-13'), 960, 1140);
  if ((day === 'Tuesday' || day === 'Thursday') && startMinute === 950) return slot(date(year === 2026 ? '05-13' : '05-14'), 960, 1140);
  if ((day === 'Tuesday' || day === 'Thursday') && (startMinute === 1060 || startMinute === 1280)) return slot(date(year === 2026 ? '05-07' : '05-08'), 1200, 1380);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 1170) return slot(date(year === 2026 ? '05-11' : '05-12'), 1200, 1380);
  if ((day === 'Tuesday' || day === 'Thursday') && startMinute === 1170) return slot(date(year === 2026 ? '05-12' : '05-13'), 1200, 1380);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 1280) return slot(date(year === 2026 ? '05-13' : '05-14'), 1200, 1380);
  return null;
}

function fall2025ClassHour(course: ScheduledCourse) {
  const first = firstWeeklyMeeting(course);
  if (!first) return null;
  const { day, startMinute } = first;
  if ((day === 'Monday' || day === 'Thursday') && startMinute === 510) return slot('2025-12-15', 480, 660);
  if ((day === 'Tuesday' || day === 'Friday') && startMinute === 730) return slot('2025-12-16', 480, 660);
  if ((day === 'Wednesday' && startMinute === 730) || (day === 'Friday' && startMinute === 840)) return slot('2025-12-17', 480, 660);
  if ((day === 'Monday' || day === 'Thursday') && startMinute === 730) return slot('2025-12-18', 480, 660);
  if ((day === 'Tuesday' || day === 'Friday') && startMinute === 620) return slot('2025-12-19', 480, 660);
  if ((day === 'Monday' || day === 'Thursday') && startMinute === 620) return slot('2025-12-22', 480, 660);
  if ((day === 'Tuesday' || day === 'Thursday') && startMinute === 840) return slot('2025-12-16', 720, 900);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 840) return slot('2025-12-17', 720, 900);
  if (((day === 'Tuesday' || day === 'Friday') && startMinute === 510) || (day === 'Wednesday' && startMinute === 620) || (day === 'Friday' && startMinute === 950)) return slot('2025-12-19', 720, 900);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 950) return slot('2025-12-22', 720, 900);
  if ((day === 'Monday' || day === 'Wednesday') && startMinute === 1170) return slot('2025-12-15', 1200, 1380);
  if ((day === 'Tuesday' || day === 'Thursday') && (startMinute === 1060 || startMinute === 1280)) return slot('2025-12-16', 1200, 1380);
  if ((day === 'Monday' || day === 'Wednesday') && (startMinute === 1060 || startMinute === 1280)) return slot('2025-12-17', 1200, 1380);
  if ((day === 'Tuesday' || day === 'Thursday') && startMinute === 1170) return slot('2025-12-18', 1200, 1380);
  return null;
}

export function deriveFinalExam(course: ScheduledCourse, termId: string): DerivedFinal {
  const code = course.examCode?.trim().toUpperCase() || null;
  const term = getFinalExamTerm(termId);
  const base = { course, examCode: code };
  if (code === 'O') return { ...base, kind: 'none', date: null, startMinute: null, endMinute: null, label: 'No final exam' };
  if (code === 'A') return { ...base, kind: 'arranged', date: null, startMinute: null, endMinute: null, label: 'By arrangement' };
  if (code === 'U') return { ...base, kind: 'project', date: null, startMinute: null, endMinute: null, label: 'Project or paper due during finals' };
  if (code === 'T') return { ...base, kind: 'multiple', date: null, startMinute: null, endMinute: null, label: 'Multiple online exam periods; confirm with instructor' };
  if (termId === 'summer-2026') return { ...base, kind: 'arranged', date: null, startMinute: null, endMinute: null, label: 'During a regular class period; confirm with instructor' };
  const group = code && term?.groupSlots?.[code];
  if (group) return { ...base, kind: 'scheduled', date: group[0], startMinute: group[1], endMinute: group[2], label: `Group exam ${code}` };
  if (code === 'C') {
    const classHour = termId === 'fall-2026'
      ? fall2026ClassHour(course)
      : termId === 'spring-2026'
        ? springClassHour(course, 2026)
        : termId === 'fall-2025'
          ? fall2025ClassHour(course)
          : termId === 'spring-2025'
            ? springClassHour(course, 2025)
            : null;
    if (classHour) return { ...base, kind: 'scheduled', ...classHour, label: 'Class-hour exam' };
  }
  return { ...base, kind: 'unresolved', date: null, startMinute: null, endMinute: null, label: code ? `Exam code ${code}; verify the section record` : 'Final exam details not listed' };
}

export function deriveFinalsSchedule(courses: ScheduledCourse[], termId: string) {
  return courses.filter((course) => !course.id.startsWith('custom:')).map((course) => deriveFinalExam(course, termId));
}

export function detectFinalExamConflicts(exams: DerivedFinal[]) {
  const scheduled = exams.filter((exam) => exam.kind === 'scheduled' && exam.date && exam.startMinute !== null && exam.endMinute !== null);
  return scheduled.flatMap((exam, index) => scheduled.slice(index + 1).filter((other) => exam.date === other.date && exam.startMinute! < other.endMinute! && other.startMinute! < exam.endMinute!).map((other) => [exam, other] as const));
}

export function formatFinalTime(minutes: number) {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(new Date(Date.UTC(2020, 0, 1, hour, minute)));
}
